import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { RootState, AppDispatch } from '@/store/store'
import { updateUserAvatar } from '@/store/slices/authSlice'
import {
    FiUpload,
    FiX,
    FiSave,
    FiMapPin,
    FiPhone,
    FiGlobe,
    FiBriefcase,
    FiPlus,
    FiTrash2,
    FiArrowLeft
} from 'react-icons/fi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
import { useImageUpload } from '@/hooks/useImageUpload'
import Avatar from '@/components/ui/Avatar'
import { clearAvatarCache } from '@/hooks/useAvatarImage'

interface ProfileFormData {
    firstName: string
    lastName: string
    bio: string
    phone: string
    website: string
    company: string
    location: {
        city: string
        address: string
    }
    skills: string[]
    portfolio: string[]
}

interface SkillSuggestion {
    category: string
    skills: string[]
}

const skillSuggestions: SkillSuggestion[] = [
    {
        category: 'Design & Creative',
        skills: ['Graphic Design', 'Logo Design', 'Web Design', 'UI/UX Design', 'Branding', 'Illustration', 'Photoshop', 'Figma', 'Adobe Creative Suite']
    },
    {
        category: 'Development & IT',
        skills: ['Web Development', 'Mobile App Development', 'JavaScript', 'React', 'Node.js', 'Python', 'Java', 'Database Management', 'DevOps']
    },
    {
        category: 'Marketing & Sales',
        skills: ['Digital Marketing', 'Social Media Marketing', 'Content Marketing', 'SEO', 'Google Ads', 'Email Marketing', 'Sales Strategy', 'Lead Generation']
    },
    {
        category: 'Writing & Content',
        skills: ['Content Writing', 'Copywriting', 'Blog Writing', 'Technical Writing', 'Social Media Content', 'Video Editing', 'Translation']
    },
    {
        category: 'Business & Admin',
        skills: ['Project Management', 'Virtual Assistant', 'Data Entry', 'Customer Service', 'Accounting', 'Business Analysis', 'Consulting']
    }
]

// Simple location system - no cities list needed

const EditProfilePage: React.FC = () => {
    const { user, isAuthenticated } = useSelector((state: RootState) => state.auth)
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const { showError, showSuccess } = useSnackbarContext()
    const { uploadAvatar, uploading: imageUploading } = useImageUpload({
        showToastMessages: false
    })

    const [formData, setFormData] = useState<ProfileFormData>({
        firstName: '',
        lastName: '',
        bio: '',
        phone: '',
        website: '',
        company: '',
        location: {
            city: '',
            address: ''
        },
        skills: [],
        portfolio: []
    })

    const [loading, setLoading] = useState(false)
    const [imagePreview, setImagePreview] = useState<string | null>(null)
    const [uploadedAvatarData, setUploadedAvatarData] = useState<any>(null)
    const [newSkill, setNewSkill] = useState('')
    const [showSkillSuggestions, setShowSkillSuggestions] = useState(false)
    const [filteredSkills, setFilteredSkills] = useState<string[]>([])
    const [newPortfolioUrl, setNewPortfolioUrl] = useState('')

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login')
            return
        }

        if (user) {
            setFormData({
                firstName: user.profile?.firstName || '',
                lastName: user.profile?.lastName || '',
                bio: user.profile?.bio || '',
                phone: user.profile?.phone || '',
                website: user.businessProfile?.website || '',
                company: user.profile?.company || '',
                location: {
                    city: user.profile?.location?.city || '',
                    address: user.profile?.location?.address || ''
                },
                skills: user.profile?.skills || [],
                portfolio: user.profile?.portfolio || []
            })

            // Don't set imagePreview from user.profile.avatar since it's an ObjectId
            // The Avatar component will handle displaying the current avatar
        }
    }, [user, isAuthenticated, navigate])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target

        if (name === 'city' || name === 'address') {
            setFormData(prev => ({
                ...prev,
                location: {
                    ...prev.location,
                    [name]: value
                }
            }))
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }))
        }
    }

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            // Validate file type and size
            if (!file.type.startsWith('image/')) {
                showError('Please select an image file')
                return
            }

            if (file.size > 10 * 1024 * 1024) { // 10MB limit (match backend)
                showError('Image size must be less than 10MB')
                return
            }

            // Create preview
            const reader = new FileReader()
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string)
            }
            reader.readAsDataURL(file)

            // File is processed immediately for upload

            // Upload avatar immediately
            try {
                const avatarData = await uploadAvatar(file)

                // Clear cache for old and new avatars
                if (user?.profile?.avatar) {
                    clearAvatarCache(user.profile.avatar)
                }
                if (avatarData._id) {
                    clearAvatarCache(avatarData._id)
                }

                // Update user avatar in Redux store
                if (avatarData._id) {
                    dispatch(updateUserAvatar(avatarData._id))
                    // Clear cache to force refresh for new avatar ID
                    clearAvatarCache()
                }

                setUploadedAvatarData(avatarData)
                showSuccess('Profile picture uploaded successfully!')
            } catch (error: any) {
                showError(error.message || 'Failed to upload profile picture')
                setImagePreview(null)
            }
        }
    }

    const handleSkillInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setNewSkill(value)

        if (value.trim()) {
            // Filter skills based on input
            const allSkills = skillSuggestions.flatMap(category => category.skills)
            const filtered = allSkills.filter(skill =>
                skill.toLowerCase().includes(value.toLowerCase()) &&
                !formData.skills.includes(skill)
            )
            setFilteredSkills(filtered.slice(0, 10))
            setShowSkillSuggestions(true)
        } else {
            setShowSkillSuggestions(false)
        }
    }

    const addSkill = (skill: string) => {
        if (skill.trim() && !formData.skills.includes(skill.trim())) {
            setFormData(prev => ({
                ...prev,
                skills: [...prev.skills, skill.trim()]
            }))
            setNewSkill('')
            setShowSkillSuggestions(false)
        }
    }

    const removeSkill = (skillToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            skills: prev.skills.filter(skill => skill !== skillToRemove)
        }))
    }

    const addPortfolioItem = () => {
        if (newPortfolioUrl.trim() && !formData.portfolio.includes(newPortfolioUrl.trim())) {
            setFormData(prev => ({
                ...prev,
                portfolio: [...prev.portfolio, newPortfolioUrl.trim()]
            }))
            setNewPortfolioUrl('')
        }
    }

    const removePortfolioItem = (urlToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            portfolio: prev.portfolio.filter(url => url !== urlToRemove)
        }))
    }

    const handleCancel = () => {
        navigate(-1) // Go back to previous page
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // Basic validation
        if (!formData.firstName.trim() || !formData.lastName.trim()) {
            showError('First name and last name are required')
            return
        }

        if (formData.phone && !formData.phone.match(/^\+?[1-9]\d{1,14}$/)) {
            showError('Please enter a valid international phone number')
            return
        }

        if (formData.website && !formData.website.match(/^https?:\/\/.+/)) {
            showError('Please enter a valid website URL (starting with http:// or https://)')
            return
        }

        setLoading(true)

        try {
            // Prepare the update data
            const updateData = {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                phone: formData.phone.trim() || undefined,
                bio: formData.bio.trim() || undefined,
                location: {
                    city: formData.location.city.trim() || undefined,
                    address: formData.location.address.trim() || undefined
                },
                skills: formData.skills,
                company: formData.company.trim() || undefined,
                website: formData.website.trim() || undefined,
                portfolio: formData.portfolio
            }

            // Call the actual API
            const { userApi } = await import('@/services/api/users')
            const response = await userApi.updateProfile(updateData)

            // @ts-expect-error - Intentional nested data structure for API response
            if (response.data?.success && response.data?.data?.user) {
                // Update Redux store with the updated user data
                const { updateUser } = await import('@/store/slices/authSlice')
                // @ts-expect-error - Intentional nested data structure for API response
                dispatch(updateUser(response.data.data.user))

                showSuccess('Profile updated successfully!')
                navigate('/dashboard/profile')
            } else {
                showError('Failed to update profile. Please try again.')
            }

        } catch (error: any) {
            console.error('Failed to update profile:', error)
            showError(error.message || 'Failed to update profile. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    if (!user) {
        return (
            <div className="flex items-center justify-center py-16">
                <LoadingSpinner size="xl" />
            </div>
        )
    }

    return (
        <>
            <Helmet>
                <title>Edit Profile - SideGO</title>
                <meta name="description" content="Update your profile information and settings" />
            </Helmet>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                <div className="mb-6 sm:mb-8">
                    <div className="flex items-center gap-4 mb-4">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-sm sm:text-base"
                        >
                            <FiArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                            Back
                        </button>
                    </div>
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                        Edit Profile
                    </h1>
                    <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                        Update your profile information to help clients find and connect with you.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                    {/* Profile Photo */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                        <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-4">
                            Profile Photo
                        </h2>

                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                            <div className="relative">
                                {imagePreview ? (
                                    // Show preview image while uploading
                                    <div className="w-24 h-24 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center overflow-hidden">
                                        <img
                                            src={imagePreview}
                                            alt="Profile preview"
                                            className="w-24 h-24 object-cover"
                                        />
                                    </div>
                                ) : (
                                    // Show current avatar using Avatar component
                                    <Avatar
                                        avatarId={user?.profile?.avatar || uploadedAvatarData?.image?._id}
                                        size="lg"
                                        showBorder={false}
                                    />
                                )}
                                {imagePreview && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setImagePreview(null)
                                        }}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                        disabled={imageUploading}
                                    >
                                        <FiX className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            <div>
                                <label className={`inline-flex items-center px-3 sm:px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors ${imageUploading
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer'
                                    }`}>
                                    {imageUploading ? (
                                        <div className="w-4 h-4 mr-2 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
                                    ) : (
                                        <FiUpload className="w-4 h-4 mr-2" />
                                    )}
                                    {imageUploading ? 'Uploading...' : 'Upload Photo'}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="sr-only"
                                        disabled={imageUploading}
                                    />
                                </label>
                                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                                    JPG, PNG, GIF or WebP (max. 10MB)
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Basic Information */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                        <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-4">
                            Basic Information
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                            <div>
                                <label htmlFor="firstName" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    First Name *
                                </label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label htmlFor="lastName" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Last Name *
                                </label>
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Phone Number
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FiPhone className="h-4 h-4 sm:h-5 sm:w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-full pl-10 pr-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="+1 234 567 8901"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="website" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Website
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FiGlobe className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="url"
                                        id="website"
                                        name="website"
                                        value={formData.website}
                                        onChange={handleInputChange}
                                        className="w-full pl-10 pr-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="https://yourwebsite.com"
                                    />
                                </div>
                            </div>

                            {user?.role === 'employer' && (
                                <div className="md:col-span-2">
                                    <label htmlFor="company" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Company Name
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FiBriefcase className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            id="company"
                                            name="company"
                                            value={formData.company}
                                            onChange={handleInputChange}
                                            className="w-full pl-10 pr-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            placeholder="Your company name"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Location */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                        <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-4">
                            Location
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                            <div>
                                <label htmlFor="city" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Location
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FiMapPin className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        id="city"
                                        name="city"
                                        value={formData.location.city}
                                        onChange={handleInputChange}
                                        className="w-full pl-10 pr-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="Enter your location"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="address" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Address (Optional)
                                </label>
                                <input
                                    type="text"
                                    id="address"
                                    name="address"
                                    value={formData.location.address}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Your specific address"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bio */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                        <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-4">
                            About You
                        </h2>

                        <div>
                            <label htmlFor="bio" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Bio
                            </label>
                            <textarea
                                id="bio"
                                name="bio"
                                value={formData.bio}
                                onChange={handleInputChange}
                                rows={4}
                                className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="Tell clients about yourself, your experience, and what makes you unique..."
                            />
                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {formData.bio.length}/500 characters
                            </p>
                        </div>
                    </div>

                    {/* Skills (for workers) */}
                    {user?.role === 'worker' && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                            <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-4">
                                Skills
                            </h2>

                            <div className="mb-4">
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={newSkill}
                                        onChange={handleSkillInputChange}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault()
                                                addSkill(newSkill)
                                            }
                                        }}
                                        className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="Add a skill (e.g., Graphic Design)"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => addSkill(newSkill)}
                                        className="absolute inset-y-0 right-0 px-2 sm:px-4 py-2 bg-primary-600 text-white rounded-r-lg hover:bg-primary-700 transition-colors"
                                    >
                                        <FiPlus className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Skill suggestions */}
                                {showSkillSuggestions && filteredSkills.length > 0 && (
                                    <div className="mt-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm">
                                        {filteredSkills.map((skill) => (
                                            <button
                                                key={skill}
                                                type="button"
                                                onClick={() => addSkill(skill)}
                                                className="block w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-600 text-xs sm:text-sm text-gray-900 dark:text-white transition-colors"
                                            >
                                                {skill}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Current skills */}
                            <div className="flex flex-wrap gap-2 mb-4">
                                {formData.skills.map((skill) => (
                                    <span
                                        key={skill}
                                        className="inline-flex items-center px-2 sm:px-3 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 text-xs sm:text-sm font-medium rounded-full"
                                    >
                                        {skill}
                                        <button
                                            type="button"
                                            onClick={() => removeSkill(skill)}
                                            className="ml-2 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
                                        >
                                            <FiX className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>

                            {/* Skill suggestions by category */}
                            <div className="space-y-3">
                                <p className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Popular skills by category:
                                </p>
                                {skillSuggestions.map((category) => (
                                    <div key={category.category}>
                                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                            {category.category}
                                        </p>
                                        <div className="flex flex-wrap gap-1">
                                            {category.skills
                                                .filter(skill => !formData.skills.includes(skill))
                                                .slice(0, 6)
                                                .map((skill) => (
                                                    <button
                                                        key={skill}
                                                        type="button"
                                                        onClick={() => addSkill(skill)}
                                                        className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded hover:bg-primary-100 dark:hover:bg-primary-900 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                                    >
                                                        + {skill}
                                                    </button>
                                                ))
                                            }
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Portfolio (for workers) */}
                    {user?.role === 'worker' && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                            <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-4">
                                Portfolio
                            </h2>

                            <div className="mb-4">
                                <div className="flex gap-2">
                                    <input
                                        type="url"
                                        value={newPortfolioUrl}
                                        onChange={(e) => setNewPortfolioUrl(e.target.value)}
                                        className="flex-1 px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="Add portfolio URL (e.g., https://behance.net/yourwork)"
                                    />
                                    <button
                                        type="button"
                                        onClick={addPortfolioItem}
                                        className="px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                                    >
                                        <FiPlus className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Current portfolio items */}
                            <div className="space-y-2">
                                {formData.portfolio.map((url, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                                    >
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs sm:text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 truncate"
                                        >
                                            {url}
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => removePortfolioItem(url)}
                                            className="ml-2 text-red-500 hover:text-red-700 transition-colors"
                                        >
                                            <FiTrash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 sm:gap-4 flex-col sm:flex-row">
                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={loading}
                            className="inline-flex items-center justify-center sm:justify-start px-4 sm:px-6 py-2 sm:py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
                        >
                            <FiX className="w-4 h-4 mr-2" />
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex items-center justify-center sm:justify-start px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
                        >
                            {loading ? (
                                <>
                                    <LoadingSpinner size="sm" className="mr-2" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <FiSave className="w-4 h-4 mr-2" />
                                    Save
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </>
    )
}

export default EditProfilePage